import get from "lodash.get";
import { useEffect } from "react";
import { RegisterOptions, UseFormReturn, useFormState } from "react-hook-form";

import SinglePickerFormField from "src/components/Pickers/SinglePickerFormField.tsx";
import { useT } from "src/internationalization";
import { BlacklistElementForm } from "src/pages/Blacklists/BlacklistsTable/BlacklistElementModal";
import { useConfList } from "src/providers/ConfListsProvider";
import { ConfListElement } from "src/types/confList";

export type ConfListSinglePickerFormFieldProps = {
  label: string;
  confListCode: string;
  helperText?: string;
  placeholder?: string;
  rules?: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled">;
  name: string;
  disabled?: boolean;
  method: UseFormReturn<BlacklistElementForm, undefined, BlacklistElementForm>;
};

export function ConfListSinglePickerFormField({
  name,
  rules,
  helperText,
  label,
  confListCode,
  disabled,
  placeholder,
  method,
}: ConfListSinglePickerFormFieldProps) {
  const { control, setValue, getValues } = method;
  const { errors } = useFormState({ control });

  const error = get(errors, name);
  const t = useT();

  const confList = useConfList(confListCode);

  useEffect(() => {
    if (confList) {
      const currentConf = getValues(name);

      if (typeof currentConf === "string" && currentConf) {
        setValue(name, {
          code: currentConf,
          name: confList.elements.find((e) => e.code === currentConf)?.name ?? "",
          disabled: false,
        });
      }
    }
  }, [confList, name, setValue, getValues]);

  return (
    <SinglePickerFormField<ConfListElement>
      autoAlign
      name={name}
      rules={rules}
      itemToString={(item) => item?.name ?? ""}
      items={confList?.elements?.filter((el) => !el.disabled) ?? []}
      loading={false}
      error={undefined}
      disabled={disabled}
      placeholder={placeholder}
      invalidText={(error?.message as string) ?? t("component.picker.conflist.invalid")}
      titleText={label}
      helperText={helperText}
    />
  );
}
